import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Search, Eye, Copy, FileDown, ChevronLeft, ChevronRight, FileText, Columns } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import { useAuthStore } from '@/store/auth.store'
import { inventoryDispatchService, type InventoryDispatchListRow } from '@/services/inventoryDispatchService'
import { ListLoader } from '@/components/shared/ListLoader'

const PAGE_SIZE = 10

type ColumnKey = 'code' | 'dispatchDate' | 'sourceWarehouse' | 'destinationWarehouse' | 'status'

const ALL_COLUMNS: { key: ColumnKey; labelKey: string }[] = [
  { key: 'code', labelKey: 'inventoryDispatch.columns.code' },
  { key: 'dispatchDate', labelKey: 'inventoryDispatch.columns.dispatchDate' },
  { key: 'sourceWarehouse', labelKey: 'inventoryDispatch.columns.sourceWarehouse' },
  { key: 'destinationWarehouse', labelKey: 'inventoryDispatch.columns.destinationWarehouse' },
  { key: 'status', labelKey: 'inventoryDispatch.columns.status' },
]

function receivingStatusLabel(status: number | undefined, t: (key: string) => string): string {
  if (status === 1) return t('inventoryDispatch.receivingStatusPending')
  if (status === 2) return t('inventoryDispatch.receivingStatusTransferred')
  if (status === 3) return t('inventoryDispatch.receivingStatusReceived')
  if (status === 4) return t('inventoryDispatch.receivingStatusCancel')
  return ''
}

export const InventoryDispatchList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = Number(user?.selectedBranch?.branchId ?? 0)

  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [rows, setRows] = useState<InventoryDispatchListRow[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>({
    code: true,
    dispatchDate: true,
    sourceWarehouse: true,
    destinationWarehouse: true,
    status: true,
  })

  useEffect(() => {
    if (!branchId) {
      setRows([])
      setTotalCount(0)
      return
    }
    let cancelled = false
    setLoading(true)
    void inventoryDispatchService
      .getAll(branchId, pageNumber, PAGE_SIZE)
      .then((res) => {
        if (cancelled) return
        setRows(res.items)
        setTotalCount(res.totalCount)
      })
      .catch((e: any) => {
        if (cancelled) return
        const status = e?.response?.status
        const msg = String(e?.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setRows([])
        setTotalCount(0)
        if (!isNoData) {
          toast.error(e?.response?.data?.message || t('inventoryDispatch.emptyTable'))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [branchId, pageNumber, t])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => {
      const statusText = receivingStatusLabel(r.receivingStatus, t).toLowerCase()
      return [r.code, r.dispatchDate, r.sourceWarehouse, r.destinationWarehouse, statusText, String(r.receivingStatus ?? '')].some((f) =>
        String(f).toLowerCase().includes(q),
      )
    })
  }, [rows, searchQuery, t])

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const page = Math.min(pageNumber, totalPages)
  const pageSlice = filtered

  const startIdx = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const endIdx = Math.min(page * PAGE_SIZE, totalCount)

  const visibleCount = Object.values(visibleColumns).filter(Boolean).length

  const toggleColumn = (key: ColumnKey) => setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }))

  const handleAdd = () => navigate('/inventory/dispatch/new')
  const handleView = (row: InventoryDispatchListRow) => navigate(`/inventory/dispatch/${row.id}/edit`)

  const handleCopy = () => {
    if (filtered.length === 0) {
      toast.error(t('inventoryDispatch.nothingToCopy'))
      return
    }
    const header = [
      t('inventoryDispatch.columns.code'),
      t('inventoryDispatch.columns.dispatchDate'),
      t('inventoryDispatch.columns.sourceWarehouse'),
      t('inventoryDispatch.columns.destinationWarehouse'),
      t('inventoryDispatch.columns.status'),
    ]
    const lines = filtered.map((r) =>
      [r.code, r.dispatchDate, r.sourceWarehouse, r.destinationWarehouse, receivingStatusLabel(r.receivingStatus, t)].join('\t'),
    )
    void navigator.clipboard.writeText([header.join('\t'), ...lines].join('\n'))
    toast.success(t('inventoryDispatch.copied'))
  }

  const handleExportCSV = () => {
    if (filtered.length === 0) {
      toast.error(t('inventoryDispatch.nothingToExport'))
      return
    }
    const header = [
      t('inventoryDispatch.columns.code'),
      t('inventoryDispatch.columns.dispatchDate'),
      t('inventoryDispatch.columns.sourceWarehouse'),
      t('inventoryDispatch.columns.destinationWarehouse'),
      t('inventoryDispatch.columns.status'),
    ].join(',')
    const body = pageSlice
      .map((r) =>
        [r.code, r.dispatchDate, r.sourceWarehouse, r.destinationWarehouse, receivingStatusLabel(r.receivingStatus, t)]
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(','),
      )
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `inventory_dispatch_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success(t('inventoryDispatch.exported'))
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('inventoryDispatch.title')}</h1>
            <p className="text-muted-foreground text-sm">{t('inventoryDispatch.listSubtitle')}</p>
          </div>
        </div>
        <Button onClick={handleAdd} className="w-full sm:w-auto shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          {t('inventoryDispatch.addDispatch')}
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium">{t('inventoryDispatch.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('inventoryDispatch.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setPageNumber(1)
                  }}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Columns className="mr-2 h-4 w-4" />
                    {t('inventoryDispatch.columnVisibility')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>{t('inventoryDispatch.columnsToggle')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {ALL_COLUMNS.map((col) => (
                    <DropdownMenuCheckboxItem key={col.key} checked={visibleColumns[col.key]} onCheckedChange={() => toggleColumn(col.key)}>
                      {t(col.labelKey)}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <FileDown className="mr-2 h-4 w-4" />
                {t('inventoryDispatch.csv')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="mr-2 h-4 w-4" />
                {t('inventoryDispatch.copy')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                  {visibleColumns.code && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryDispatch.columns.code')}
                    </TableHead>
                  )}
                  {visibleColumns.dispatchDate && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryDispatch.columns.dispatchDate')}
                    </TableHead>
                  )}
                  {visibleColumns.sourceWarehouse && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryDispatch.columns.sourceWarehouse')}
                    </TableHead>
                  )}
                  {visibleColumns.destinationWarehouse && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryDispatch.columns.destinationWarehouse')}
                    </TableHead>
                  )}
                  {visibleColumns.status && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryDispatch.columns.status')}
                    </TableHead>
                  )}
                  <TableHead className="h-12 w-[88px] bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    {t('inventoryDispatch.columns.view')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleCount + 1} className="p-0">
                      <ListLoader message={t('inventoryDispatch.loading', { defaultValue: 'Loading…' })} />
                    </TableCell>
                  </TableRow>
                ) : pageSlice.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleCount + 1} className="py-12 text-center text-muted-foreground">
                      {t('inventoryDispatch.emptyTable')}
                    </TableCell>
                  </TableRow>
                ) : (
                  pageSlice.map((row) => (
                    <TableRow key={row.id} className="border-b transition-colors hover:bg-primary/5 dark:hover:bg-primary/10">
                      {visibleColumns.code && <TableCell className="px-4 py-2 text-center text-sm">{row.code}</TableCell>}
                      {visibleColumns.dispatchDate && <TableCell className="px-4 py-2 text-center text-sm">{row.dispatchDate}</TableCell>}
                      {visibleColumns.sourceWarehouse && <TableCell className="px-4 py-2 text-center text-sm">{row.sourceWarehouse}</TableCell>}
                      {visibleColumns.destinationWarehouse && (
                        <TableCell className="px-4 py-2 text-center text-sm">{row.destinationWarehouse}</TableCell>
                      )}
                      {visibleColumns.status && (
                        <TableCell className="px-4 py-2 text-center text-sm">
                          {receivingStatusLabel(row.receivingStatus, t) || '—'}
                        </TableCell>
                      )}
                      <TableCell className="px-2 py-2 text-center">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleView(row)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col items-center justify-between gap-4 border-t px-4 py-4 sm:flex-row sm:px-6">
            <p className="text-center text-sm text-muted-foreground sm:text-left">
              {t('inventoryDispatch.showing', { start: startIdx, end: endIdx, total: totalCount })}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPageNumber((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-[2rem] text-center text-sm font-medium">{page}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

